/**
 * Laabha — streaming client for axon-tally-chat-bll.
 *
 * Replaces the old blocking /api/v1/aiaccountant/agent path with real SSE
 * streaming via the Anthropic Agents SDK BLL at /tally-chat-bll/.
 *
 * Flow:
 *   1. POST /tally-chat-bll/v1/conversations  — create session
 *   2. POST /tally-chat-bll/v1/conversations/{id}/stream  — multipart SSE
 *
 * Event format from the BLL (data-only, no event: prefix line):
 *   data: {"type": "text_delta",    "text": "..."}
 *   data: {"type": "thinking_delta","text": "..."}
 *   data: {"type": "tool_use",      "id":"...", "tool":"...", "input":{}}
 *   data: {"type": "tool_result",   "id":"...", "preview":"...", "is_error":false}
 *   data: {"type": "done",          "final_text":"...", "is_error":false, "total_cost_usd":0.04}
 *   data: {"type": "error",         "message": "..."}
 */

import { getCachedUser } from './auth';

const BLL_BASE = '/tally-chat-bll';
// Internal service token — gates access to the BLL (not a user secret).
const BLL_TOKEN = 'e1d68d4530550dce2e22ead801ba889633cbe27c3967d9bc1ebf40e4d5412b7c';
// Tally MCP server inside axon-aiaccountant container (stable container IP).
const TALLY_MCP_URL = 'http://172.25.0.23:8275/mcp';

export interface TallyBllStreamCallbacks {
  /** Called for each streamed token. */
  onTextDelta: (text: string) => void;
  /** Called when the agent invokes a Tally tool. */
  onToolUse: (tool: string, id: string) => void;
  /** Called when a tool call completes. */
  onToolResult: (id: string, preview: string, isError: boolean) => void;
  /** Called with agent thinking text (optional display). */
  onThinkingDelta?: (text: string) => void;
  /** Called when the stream ends (success or agent-level error). */
  onDone: (result: { finalText: string; isError: boolean; costUsd?: number }) => void;
  /** Called on network / auth / unexpected errors. */
  onError: (message: string) => void;
  /** Called once when the BLL conversation is created (or re-used). Persist this id for follow-ups. */
  onConversationCreated?: (convId: string) => void;
}

export interface TallyBllStreamHandle {
  cancel: () => void;
}

/** Create a tally-bll conversation and stream the agent response. */
export function streamTallyAgent(opts: {
  message: string;
  file?: File;
  tallyUrl: string;
  companyId: string;
  bankLedger?: string;
  bankId?: string;
  /** If provided, skips conversation creation and continues an existing BLL session. */
  existingConvId?: string;
}, cb: TallyBllStreamCallbacks): TallyBllStreamHandle {
  const ctrl = new AbortController();

  (async () => {
    const tenantId = getCachedUser()?.email ?? 'laabha-default';

    // ── 1. Create or reuse conversation ────────────────────────────────────
    let convId: string;
    if (opts.existingConvId) {
      convId = opts.existingConvId;
      cb.onConversationCreated?.(convId);
    } else {
      try {
        const res = await fetch(`${BLL_BASE}/v1/conversations`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${BLL_TOKEN}`,
            'X-Tenant-Id': tenantId,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            title: `Import ${new Date().toISOString().slice(0, 16)}`,
            mcp_servers: { tally: { type: 'http', url: TALLY_MCP_URL } },
            max_turns: 100,
          }),
          signal: ctrl.signal,
        });
        if (!res.ok) throw new Error(`Session create failed: ${res.status}`);
        const body = await res.json();
        convId = body.data?.id;
        if (!convId) throw new Error('No conversation id returned');
        cb.onConversationCreated?.(convId);
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        cb.onError(`Could not start agent session: ${(err as Error).message}`);
        return;
      }
    }

    // ── 2. Build enriched prompt with Tally context ────────────────────────
    const contextLines: string[] = [];
    if (opts.tallyUrl)   contextLines.push(`Tally URL: ${opts.tallyUrl}`);
    if (opts.companyId)  contextLines.push(`Active Company: ${opts.companyId}`);
    if (opts.bankLedger) contextLines.push(`Bank Ledger: ${opts.bankLedger}`);
    if (opts.bankId)     contextLines.push(`Bank format: ${opts.bankId.toUpperCase()}`);

    const prompt = contextLines.length
      ? `${contextLines.join('\n')}\n\n${opts.message}`
      : opts.message;

    // ── 3. Stream the message ──────────────────────────────────────────────
    const fd = new FormData();
    fd.append('prompt', prompt);
    if (opts.file) fd.append('files', opts.file, opts.file.name);

    let resp: Response;
    try {
      resp = await fetch(`${BLL_BASE}/v1/conversations/${convId}/stream`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${BLL_TOKEN}`,
          'X-Tenant-Id': tenantId,
        },
        body: fd,
        signal: ctrl.signal,
      });
      if (!resp.ok || !resp.body) {
        cb.onError(`Stream failed: ${resp.status}`);
        return;
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      cb.onError((err as Error).message);
      return;
    }

    // ── 4. Parse SSE frames ────────────────────────────────────────────────
    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    let accText = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });

        let boundary = buf.indexOf('\n\n');
        while (boundary !== -1) {
          const frame = buf.slice(0, boundary);
          buf = buf.slice(boundary + 2);
          boundary = buf.indexOf('\n\n');

          const dataLine = frame.split('\n').find((l) => l.startsWith('data:'));
          if (!dataLine) continue;

          let ev: Record<string, unknown>;
          try { ev = JSON.parse(dataLine.slice(5).trim()); } catch { continue; }

          const t = ev.type as string;

          if (t === 'text_delta' && typeof ev.text === 'string') {
            accText += ev.text;
            cb.onTextDelta(ev.text);
          } else if (t === 'thinking_delta' && typeof ev.text === 'string') {
            cb.onThinkingDelta?.(ev.text);
          } else if (t === 'tool_use') {
            cb.onToolUse(String(ev.tool ?? 'tool'), String(ev.id ?? ''));
          } else if (t === 'tool_result') {
            cb.onToolResult(
              String(ev.id ?? ''),
              String(ev.preview ?? ''),
              Boolean(ev.is_error),
            );
          } else if (t === 'done') {
            cb.onDone({
              finalText: (ev.final_text as string | undefined) ?? accText,
              isError: Boolean(ev.is_error),
              costUsd: typeof ev.total_cost_usd === 'number' ? ev.total_cost_usd : undefined,
            });
            return;
          } else if (t === 'error') {
            cb.onError(String(ev.message ?? 'Agent error'));
            return;
          }
        }
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      cb.onError((err as Error).message);
    }
  })();

  return { cancel: () => ctrl.abort() };
}
